/**
 * @file    widgetDraftBuilder.ts
 * @scope   SHARED
 * @summary Pure builder that turns a catalogue widget into the draft published when the user
 *          switches the chart type.
 * @consumes models/WidgetModel (types only)
 * @produces buildChartTypeWidgetDraft
 * @sideEffects None. Inputs are never mutated; a brand-new draft is returned.
 * @notes Extracted from `useChartTypeChangeEffect` so the widget-shaping rules can be read and
 *        tested without a React effect around them. Behaviour is pinned by
 *        ../hooks/useChartTypeChangeEffect.test.ts.
 */
import { Widget } from 'models/WidgetModel';

/**
 * Catalogue metadata that must never leak into the widget draft.
 * WHY: these describe the entry in the widget library, not the widget the user is editing.
 */
const CATALOGUE_ONLY_FIELDS = ['viewId', 'image', 'displayName', 'isCustom'] as const;

/** Grid geometry of the widget being edited; it must survive a chart-type swap. */
const GEOMETRY_FIELDS = ['i', 'x', 'y', 'w', 'h'] as const;

/** Inputs of {@link buildChartTypeWidgetDraft}. */
export type BuildChartTypeWidgetDraftParams = {
  /** Result of `initWidget` for the newly selected chart type. */
  initializedWidget: any;
  /** Widget draft currently being edited. */
  customWidget: any;
  /** Preview data source already built for the new chart type. */
  previewDataSource: any;
  /** Guardrails `DataSource` value resolved for the new chart type. */
  dataSource: string;
  /** `true` when the new selection is a child chart. */
  isChild: boolean;
  /** Options resolved for the new chart type. */
  options: any;
  /** Colour palette resolved for the new chart type. */
  colors: any;
};

/**
 * Builds the widget draft for a newly selected chart type.
 *
 * The merge order is significant and intentionally preserved: the edited widget provides the
 * base, the catalogue widget overrides it, and the preview data source wins last.
 */
export const buildChartTypeWidgetDraft = ({
  initializedWidget,
  customWidget,
  previewDataSource,
  dataSource,
  isChild,
  options,
  colors,
}: BuildChartTypeWidgetDraftParams): Widget => {
  const catalogueWidget: any = { ...initializedWidget };

  CATALOGUE_ONLY_FIELDS.forEach((field) => delete catalogueWidget[field]);
  // QUIRK: `description` is stripped only when truthy, so an empty description survives. Kept as
  // is on purpose — see the characterization test before changing it.
  if (catalogueWidget.description) delete catalogueWidget.description;

  catalogueWidget.options = options;
  catalogueWidget.colors = colors;
  catalogueWidget.config = {
    ...initializedWidget.config,
    ...buildGeometry(customWidget),
  };

  return {
    ...customWidget,
    customWidgetConfig: {
      ...customWidget.customWidgetConfig,
      dataSource: dataSource,
      metadata: {
        ...customWidget.customWidgetConfig?.metadata,
        isChild,
      },
    },
    ...catalogueWidget,
    ...previewDataSource,
  } as Widget;
};

/** Copies the grid geometry of the widget being edited, preserving `undefined` entries. */
const buildGeometry = (customWidget: any): Record<string, unknown> =>
  GEOMETRY_FIELDS.reduce<Record<string, unknown>>((geometry, field) => {
    geometry[field] = customWidget?.config?.[field];
    return geometry;
  }, {});
